// Image entity geometry: rotated placement rect, hit-testing, world↔source-pixel mapping.
// Lives in doc/ (pure derived math) so io/ exporters may use it (ARC-3a).
import { rotate, type Vec } from "../core/geometry";
import { pointInPolygon } from "../core/intersect";
import type { ImageEntity } from "./entities";

/** World-space corners TL,TR,BR,BL including rotation about the image center. */
export function imageCorners(e: ImageEntity): Vec[] {
  const raw: Vec[] = [
    { x: e.pos.x, y: e.pos.y },
    { x: e.pos.x + e.wMm, y: e.pos.y },
    { x: e.pos.x + e.wMm, y: e.pos.y + e.hMm },
    { x: e.pos.x, y: e.pos.y + e.hMm }
  ];
  if (Math.abs(e.rotationDeg) < 1e-9) return raw;
  const center = { x: e.pos.x + e.wMm / 2, y: e.pos.y + e.hMm / 2 };
  return raw.map((p) => rotate(p, center, e.rotationDeg));
}

export function imageCenter(e: ImageEntity): Vec {
  return { x: e.pos.x + e.wMm / 2, y: e.pos.y + e.hMm / 2 };
}

export function pointInImage(e: ImageEntity, p: Vec): boolean {
  return pointInPolygon(p, imageCorners(e));
}

/**
 * World point → source-pixel coordinates of the DISPLAYED bitmap (rectified when a
 * homography is set). srcW/srcH are the displayed bitmap's pixel dimensions.
 */
export function worldToImagePx(e: ImageEntity, p: Vec, srcW: number, srcH: number): Vec {
  const center = imageCenter(e);
  const local = Math.abs(e.rotationDeg) < 1e-9 ? p : rotate(p, center, -e.rotationDeg);
  return {
    x: ((local.x - e.pos.x) / e.wMm) * srcW,
    y: ((local.y - e.pos.y) / e.hMm) * srcH
  };
}

export function imagePxToWorld(e: ImageEntity, px: Vec, srcW: number, srcH: number): Vec {
  const local = {
    x: e.pos.x + (px.x / srcW) * e.wMm,
    y: e.pos.y + (px.y / srcH) * e.hMm
  };
  if (Math.abs(e.rotationDeg) < 1e-9) return local;
  return rotate(local, imageCenter(e), e.rotationDeg);
}

/**
 * IMG-6 crop: two world points → { pos, wMm, hMm, crop } that trim the image to the
 * selected sub-rectangle, keeping the cropped content visually in place (rotation-exact).
 * Returns null when the selection is too small.
 */
export function cropPatch(
  e: ImageEntity,
  worldA: Vec,
  worldB: Vec
): { pos: Vec; wMm: number; hMm: number; crop: { x: number; y: number; w: number; h: number } } | null {
  const center = imageCenter(e);
  const rot = e.rotationDeg;
  // world → unrotated local absolute coords
  const localA = Math.abs(rot) < 1e-9 ? worldA : rotate(worldA, center, -rot);
  const localB = Math.abs(rot) < 1e-9 ? worldB : rotate(worldB, center, -rot);
  const lx0 = Math.max(e.pos.x, Math.min(localA.x, localB.x));
  const ly0 = Math.max(e.pos.y, Math.min(localA.y, localB.y));
  const lx1 = Math.min(e.pos.x + e.wMm, Math.max(localA.x, localB.x));
  const ly1 = Math.min(e.pos.y + e.hMm, Math.max(localA.y, localB.y));
  const newW = lx1 - lx0;
  const newH = ly1 - ly0;
  if (newW < e.wMm * 0.02 || newH < e.hMm * 0.02) return null; // guard tiny crops

  // compose with any existing crop (fractions of the current placed box)
  const cur = e.crop ?? { x: 0, y: 0, w: 1, h: 1 };
  const fax = (lx0 - e.pos.x) / e.wMm;
  const fay = (ly0 - e.pos.y) / e.hMm;
  const fbx = (lx1 - e.pos.x) / e.wMm;
  const fby = (ly1 - e.pos.y) / e.hMm;
  const crop = {
    x: cur.x + fax * cur.w,
    y: cur.y + fay * cur.h,
    w: (fbx - fax) * cur.w,
    h: (fby - fay) * cur.h
  };

  // rotation-exact new top-left: keeps cropped content where it was on screen
  const half = { x: newW / 2, y: newH / 2 };
  const cropTLworld = Math.abs(rot) < 1e-9 ? { x: lx0, y: ly0 } : rotate({ x: lx0, y: ly0 }, center, rot);
  const rHalf = rotate(half, { x: 0, y: 0 }, rot);
  const pos = { x: cropTLworld.x - half.x + rHalf.x, y: cropTLworld.y - half.y + rHalf.y };
  return { pos, wMm: newW, hMm: newH, crop };
}
